import { useState, useMemo, useEffect, useRef } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  ShoppingCart, Users, TrendingUp, DollarSign,
  ArrowUpRight, ArrowDownRight, MoveHorizontal, Plus,
  Ticket, Megaphone, Package, Globe,
  Palette, PartyPopper, Check, Calendar, GitCompareArrows,
  type LucideIcon,
} from 'lucide-react';
import { analyticsApi } from '../../api/endpoints/analytics';
import { productsApi } from '../../api/endpoints/products';
import { ordersApi } from '../../api/endpoints/orders';
import { useStore } from '../../contexts/StoreContext';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { DELIVERY_STATUSES, DELIVERY_STATUS_CLASSES } from '../../lib/constants';
import { AppWidgets } from './AppWidgets';
import clsx from 'clsx';

/* ════════════════════════════════════════════════════════════
   Formatting helpers
   ════════════════════════════════════════════════════════════ */

function SarIcon({ className = 'inline-block w-[0.95em] h-[0.95em] -translate-y-[1px]' }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1124.14 1256.39" fill="currentColor" className={className} aria-hidden>
      <path d="M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z"/>
      <path d="M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z"/>
    </svg>
  );
}

const intFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const floatFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function FmtNum({ amount }: { amount: number }) {
  if (!Number.isFinite(amount)) return <>0</>;
  const hasDecimals = amount % 1 !== 0;
  return <span className="num">{(hasDecimals ? floatFmt : intFmt).format(amount)}</span>;
}

function FmtSar({ amount, className = '' }: { amount: number; className?: string }) {
  return <span className={className}><FmtNum amount={amount} /><span className="ui-sar"><SarIcon /></span></span>;
}

function FmtCompact({ amount }: { amount: number }) {
  if (!Number.isFinite(amount)) return <span className="num">0</span>;
  if (amount >= 1_000_000) return <span className="num">{(amount / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M</span>;
  if (amount >= 1_000)     return <span className="num">{(amount / 1_000).toLocaleString('en-US',     { maximumFractionDigits: 1 })}K</span>;
  return <span className="num">{amount.toLocaleString('en-US')}</span>;
}

function relativeTime(iso: string, lang: string): string {
  const date = new Date(iso).getTime();
  if (Number.isNaN(date)) return '-';
  const diff = (Date.now() - date) / 1000;
  const rtf = new Intl.RelativeTimeFormat(lang === 'ar' ? 'ar' : 'en', { numeric: 'auto' });
  if (diff < 60)      return rtf.format(-Math.round(diff), 'second');
  if (diff < 3600)    return rtf.format(-Math.round(diff / 60), 'minute');
  if (diff < 86400)   return rtf.format(-Math.round(diff / 3600), 'hour');
  if (diff < 604800)  return rtf.format(-Math.round(diff / 86400), 'day');
  if (diff < 2592000) return rtf.format(-Math.round(diff / 604800), 'week');
  return rtf.format(-Math.round(diff / 2592000), 'month');
}

/* ════════════════════════════════════════════════════════════
   Small reusable pieces
   ════════════════════════════════════════════════════════════ */

/** Sparkline - 14 points, 32px tall, signature pop stroke. */
function Sparkline({ points, color = 'var(--color-pop-fg)', width = 120, height = 32 }: { points: number[]; color?: string; width?: number; height?: number }) {
  // `width` is only the coordinate space for the path math + viewBox — the
  // SVG itself renders at 100% of its container. Previously the SVG was
  // rendered at a fixed pixel width, so on narrow viewports the sparkline
  // overflowed its parent card and spilled past the right edge.
  const n = points.length;
  const padded = n > 0 ? points.slice(-14) : [];
  const pts = padded.length ? padded : Array(14).fill(0);
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const range = max - min || 1;
  const stepX = width / Math.max(pts.length - 1, 1);
  const coords = pts.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as const;
  });
  const d = coords.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
  const areaD = `${d} L${width},${height} L0,${height} Z`;
  const gradId = `sp-${Math.random().toString(36).slice(2, 8)}`;
  const flat = max === min;
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="block w-full"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {!flat && <path d={areaD} fill={`url(#${gradId})`} />}
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity={flat ? 0.45 : 1} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Delta pill: up / down / flat. Renders nothing when there's no comparison
   point — a "↔ —" placeholder reads as a broken glyph on a fresh store. */
function DeltaPill({ pct }: { pct: number | null }) {
  if (pct == null || !Number.isFinite(pct)) return null;
  if (pct > 0) return <span className="delta delta-up"><ArrowUpRight className="h-3 w-3" />{pct.toFixed(1)}%</span>;
  if (pct < 0) return <span className="delta delta-down"><ArrowDownRight className="h-3 w-3" />{Math.abs(pct).toFixed(1)}%</span>;
  return <span className="delta delta-flat"><MoveHorizontal className="h-3 w-3" />0%</span>;
}

/** Section title (medium heading). */
function CardHead({ title, action, emoji }: { title: string; emoji?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-[14px] font-bold text-text-primary">
        {title}{emoji ? ` ${emoji}` : ''}
      </h2>
      {action}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Period selector
   ════════════════════════════════════════════════════════════ */

// `Period` is either one of the four presets, or a dynamic code like
// 'year-2024' / 'month-2024-03' written by the custom picker. The picker
// codes pass straight through to the backend's dynamic-period parser.
type Period = 'today' | 'month' | 'year' | 'all' | `year-${number}` | `month-${number}-${string}`;

const PRESET_PERIODS = ['today', 'month', 'year', 'all'] as const;

function periodToBackendCode(p: Period): string {
  switch (p) {
    case 'today': return 'today';
    case 'month': return 'month-current';
    case 'year':  return 'year-current';
    case 'all':   return 'all';
    default:      return p; // 'year-YYYY' or 'month-YYYY-MM' passes through
  }
}

function parseDynamicPeriod(p: Period): { kind: 'year'; year: number } | { kind: 'month'; year: number; month: number } | null {
  const y = /^year-(\d{4})$/.exec(p);
  if (y) return { kind: 'year', year: Number(y[1]) };
  const m = /^month-(\d{4})-(\d{2})$/.exec(p);
  if (m) return { kind: 'month', year: Number(m[1]), month: Number(m[2]) };
  return null;
}

function PeriodSelector({ value, onChange, onPrefetch, isRefreshing, createdAt, lang, compareEnabled, onToggleCompare }:
{ value: Period; onChange: (p: Period) => void; onPrefetch?: (p: Period) => void; isRefreshing?: boolean; createdAt?: string; lang: string; compareEnabled: boolean; onToggleCompare: () => void }) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const options: { id: Period; label: string }[] = [
    { id: 'today', label: t('dashboard.periodToday', 'اليوم') },
    { id: 'month', label: t('dashboard.periodMonth', 'هذا الشهر') },
    { id: 'year',  label: t('dashboard.periodYear', 'هذه السنة') },
    { id: 'all',   label: t('dashboard.periodAll', 'كل الوقت') },
  ];

  // Close the picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  const dynamic = parseDynamicPeriod(value);
  const presetActive = (PRESET_PERIODS as readonly string[]).includes(value);

  return (
    <div className="flex items-center gap-2 self-start">
      <div className="ui-rail inline-flex gap-1">
        {options.map(o => (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            onMouseEnter={() => onPrefetch?.(o.id)}
            onFocus={() => onPrefetch?.(o.id)}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors whitespace-nowrap',
              value === o.id ? 'text-accent-ink' : 'text-text-muted hover:text-text-primary',
            )}
            style={value === o.id ? { background: 'var(--color-pop-fg)' } : undefined}
          >
            {o.label}
          </button>
        ))}
        {/* Selected custom period (e.g., "March 2024") becomes its own pill. */}
        {dynamic && !presetActive && (
          <span
            className="rounded-lg px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap text-accent-ink"
            style={{ background: 'var(--color-pop-fg)' }}
          >
            {formatDynamicPeriodLabel(dynamic, lang)}
          </span>
        )}
      </div>

      <div className="relative" ref={pickerRef}>
        <button
          type="button"
          onClick={() => setPickerOpen(o => !o)}
          aria-label={t('dashboard.customPeriod', 'اختيار فترة محددة')}
          title={t('dashboard.customPeriod', 'اختيار فترة محددة')}
          className={clsx(
            'ui-icon-btn',
            !presetActive && 'text-accent-pop',
          )}
        >
          <Calendar className="h-4 w-4" />
        </button>
        {pickerOpen && (
          <CustomPeriodPopover
            createdAt={createdAt}
            onPick={(p) => { onChange(p); setPickerOpen(false); }}
          />
        )}
      </div>

      <button
        type="button"
        onClick={onToggleCompare}
        aria-pressed={compareEnabled}
        title={t('dashboard.compareToggle', 'مقارنة بالفترة السابقة')}
        className={clsx(
          'ui-icon-btn',
          compareEnabled ? 'text-accent-pop' : 'text-text-muted/70',
        )}
      >
        <GitCompareArrows className="h-4 w-4" />
      </button>

      {isRefreshing && (
        <span
          className="inline-block h-3 w-3 rounded-full border-2 border-text-muted/30 border-t-text-muted animate-spin"
          aria-label={t('common.loading', 'جارٍ التحميل')}
        />
      )}
    </div>
  );
}

function formatDynamicPeriodLabel(
  parsed: NonNullable<ReturnType<typeof parseDynamicPeriod>>,
  lang: string,
): string {
  if (parsed.kind === 'year') return String(parsed.year);
  const d = new Date(parsed.year, parsed.month - 1, 1);
  return d.toLocaleDateString(lang === 'ar' ? 'ar' : 'en', { year: 'numeric', month: 'long' });
}

function CustomPeriodPopover({ createdAt, onPick }: { createdAt?: string; onPick: (p: Period) => void }) {
  const { t, i18n } = useTranslation();
  const created = createdAt ? new Date(createdAt) : null;
  const thisYear = new Date().getFullYear();
  const minYear = created ? created.getFullYear() : thisYear - 5;
  const years: number[] = [];
  for (let y = thisYear; y >= minYear; y--) years.push(y);

  return (
    <div
      className="absolute end-0 top-full mt-2 z-30 rounded-xl border border-border-default bg-bg-secondary shadow-lg w-64 p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-[11px] font-semibold text-text-muted mb-2">
        {t('dashboard.pickMonth', 'اختر شهر')}
      </p>
      <input
        type="month"
        min={created ? `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}` : undefined}
        max={`${thisYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
        onChange={(e) => {
          const v = e.target.value;
          if (v) onPick(`month-${v}` as Period);
        }}
        className="w-full rounded-lg border border-border-default bg-bg-tertiary px-3 py-1.5 text-[13px] text-text-primary"
        lang={i18n.language}
      />
      <div className="my-3 h-px bg-border-default/50" />
      <p className="text-[11px] font-semibold text-text-muted mb-2">
        {t('dashboard.pickYear', 'أو اختر سنة')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {years.map(y => (
          <button
            key={y}
            type="button"
            onClick={() => onPick(`year-${y}` as Period)}
            className="rounded-lg px-2.5 py-1 text-[12px] font-semibold text-text-secondary hover:text-text-primary"
            style={{ background: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-default)' }}
          >
            {y}
          </button>
        ))}
      </div>
    </div>
  );
}

function computeElapsedLabel(period: Period, createdAt: string | undefined, t: TFunction): string {
  const now = new Date();
  switch (period) {
    case 'today': {
      const hours = now.getHours();
      return t('dashboard.elapsedHours', '{{count}} ساعة من اليوم', { count: hours });
    }
    case 'month': {
      const days = now.getDate();
      return t('dashboard.elapsedDays', '{{count}} يوم من الشهر', { count: days });
    }
    case 'year': {
      const months = now.getMonth();
      const days = now.getDate();
      return t('dashboard.elapsedMonthsDays', '{{months}} شهر و{{days}} يوم من السنة', { months, days });
    }
    case 'all': {
      if (!createdAt) return '';
      const created = new Date(createdAt);
      const months = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
      return t('dashboard.elapsedSinceStart', 'منذ {{count}} شهر', { count: Math.max(months, 0) });
    }
    default: {
      // Dynamic codes — fully-past calendar windows, no "elapsed" semantics.
      return '';
    }
  }
}

/* ════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════ */

export function DashboardHome() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentStore } = useStore();
  const { user } = useAuth();
  const go = (path: string) => navigate(`/${storeId}${path}`);

  // Delivery status, not payment status. The widget fetches with no `status`
  // param, and the API defaults that to paid-only, so `o.status` is the
  // constant 'completed' for every row and can't be filtered on.
  const [orderFilter, setOrderFilter] = useState<string>('all');
  const [period, setPeriod] = useState<Period>('month');

  // Compare-vs-previous-period toggle. Persisted per account so a merchant
  // who'd rather not see deltas keeps that off across sessions/devices that
  // share the same login. Saved under the userId so two accounts on the
  // same browser don't share preferences.
  const compareKey = `dashboard_compare_${user?.userId ?? 'anon'}`;
  const [compareEnabled, setCompareEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(compareKey);
    return stored === null ? true : stored === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(compareKey, compareEnabled ? '1' : '0');
  }, [compareKey, compareEnabled]);

  /* ── Data ─────────────────────────────────────────────────── */
  // Backend's /analytics endpoint is now period-aware (calendar codes), so
  // every period — including 'month' — goes through one query. The /widgets
  // endpoint is no longer hit from the home page.
  const periodQuery = useQuery({
    queryKey: ['dashboard-period', storeId, period],
    queryFn: () => analyticsApi.getAnalytics(storeId!, periodToBackendCode(period)).catch(() => null),
    enabled: !!storeId,
    retry: false,
    // Heavy ranges (this-year, prev-year) take a while server-side. Keeping
    // the previous period's data on screen during the refetch avoids a full
    // blank state when the user clicks between periods.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // Only block the page on the FIRST load (no data yet). Subsequent period
  // swaps surface as an inline "updating" badge on the selector.
  const initialLoading = periodQuery.isLoading && !periodQuery.data;
  const isRefreshing = periodQuery.isFetching && !!periodQuery.data;
  const periodAnalytics = periodQuery.data;

  // Prefetch on hover so the first click on a heavy period feels instant once
  // the user has cursored over the button.
  const queryClient = useQueryClient();
  const prefetchPeriod = (p: Period) => {
    if (!storeId || p === period) return;
    queryClient.prefetchQuery({
      queryKey: ['dashboard-period', storeId, p],
      queryFn: () => analyticsApi.getAnalytics(storeId, periodToBackendCode(p)).catch(() => null),
      staleTime: 60_000,
    });
  };

  const { data: productsData } = useQuery({
    queryKey: ['products-count', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 1 }),
    enabled: !!storeId,
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['dashboard-analytics', storeId],
    queryFn: () => analyticsApi.getAnalytics(storeId!).catch(() => null),
    enabled: !!storeId,
    retry: false,
  });

  const { data: ordersData } = useQuery({
    queryKey: ['dashboard-orders', storeId],
    queryFn: () => ordersApi.list(storeId!, { limit: 10 }).catch(() => null),
    enabled: !!storeId,
    retry: false,
  });

  const elapsedLabel = useMemo(
    () => computeElapsedLabel(period, currentStore?.createdAt, t),
    [period, currentStore?.createdAt, t],
  );

  if (initialLoading) return <LoadingSpinner fullPage />;

  const stats = periodAnalytics?.stats;
  const chartData = periodAnalytics?.timeSeries ?? [];
  const recentOrders = ordersData?.orders ?? [];
  const topProducts = analyticsData?.topProducts ?? [];
  const trafficData = (analyticsData?.traffic ?? null) as { pageViews?: number; uniqueVisitors?: number } | null;
  const trafficSources = (analyticsData as any)?.trafficSources as { source: string; visits: number; percentage: number }[] | undefined;
  const currency = currentStore?.currency || 'SAR';

  const revenue = stats?.revenue ?? 0;
  const orders = stats?.orders ?? 0;
  const customers = stats?.customers ?? 0;
  const avgOrder = stats?.averageOrderValue ?? 0;
  const previousRevenue = stats?.previousRevenue ?? null;
  const previousOrders = stats?.previousOrders ?? null;
  const previousCustomers = stats?.previousCustomers ?? null;
  const previousAvg = stats?.previousAverageOrderValue ?? null;

  // When the compare toggle is off we propagate null deltas everywhere; the
  // DeltaPill component already renders nothing for null, and HeroRevenue
  // falls back to the neutral hint (no red-when-it's-only-9am pessimism).
  const revenueDelta = compareEnabled && previousRevenue && previousRevenue > 0 ? ((revenue - previousRevenue) / previousRevenue) * 100 : null;
  const ordersDelta = compareEnabled && previousOrders && previousOrders > 0 ? ((orders - previousOrders) / previousOrders) * 100 : null;
  const customersDelta = compareEnabled && previousCustomers && previousCustomers > 0 ? ((customers - previousCustomers) / previousCustomers) * 100 : null;
  const avgDelta = compareEnabled && previousAvg && previousAvg > 0 ? ((avgOrder - previousAvg) / previousAvg) * 100 : null;

  const revenueSeries = chartData.map(p => p.revenue ?? 0);
  const ordersSeries = chartData.map(p => p.orders ?? 0);

  /* ── Monthly goal ─────────────────────────────────────────── */
  const goalKey = `monthly_goal_${storeId}`;
  const savedGoal = typeof window !== 'undefined' ? localStorage.getItem(goalKey) : null;
  const monthlyGoal = savedGoal !== null
    ? Number(savedGoal)
    : revenue < 500 ? 500
    : revenue < 1000 ? 1000
    : revenue < 5000 ? 5000
    : revenue < 10000 ? 10000
    : Math.ceil(revenue * 1.2 / 10000) * 10000;

  const goalPct = monthlyGoal > 0 ? Math.min(Math.round((revenue / monthlyGoal) * 100), 100) : 0;
  const goalRemaining = Math.max(0, monthlyGoal - revenue);

  /* ── Filtered orders ──────────────────────────────────────── */
  const filteredOrders = orderFilter === 'all'
    ? recentOrders
    : recentOrders.filter((o: any) => (o.delivery || o.status) === orderFilter);

  /* ── Setup checklist state ────────────────────────────────── */
  const setupSteps = [
    { id: 'products',   labelKey: 'setup.addProducts',    descKey: 'setup.addProductsDesc',    action: '/products', done: (productsData?.total ?? 0) > 0 || (productsData?.products?.length ?? 0) > 0, icon: Package },
    { id: 'customize',  labelKey: 'setup.customizeStore', descKey: 'setup.customizeStoreDesc', action: '/settings', done: !!currentStore?.logo, icon: Palette },
    { id: 'firstOrder', labelKey: 'setup.firstOrder',     descKey: 'setup.firstOrderDesc',     action: '/orders',    done: recentOrders.length > 0 || orders > 0, icon: PartyPopper },
  ];
  const setupDone = setupSteps.filter(s => s.done).length;
  const setupPct  = Math.round((setupDone / setupSteps.length) * 100);

  const periodLabel = (() => {
    const dynamic = parseDynamicPeriod(period);
    if (dynamic) return formatDynamicPeriodLabel(dynamic, i18n.language);
    switch (period) {
      case 'today': return t('dashboard.periodToday', 'اليوم');
      case 'month': return t('dashboard.periodMonth', 'هذا الشهر');
      case 'year':  return t('dashboard.periodYear', 'هذه السنة');
      case 'all':   return t('dashboard.periodAll', 'كل الوقت');
      default:      return String(period);
    }
  })();

  return (
    <div className="space-y-6">
      {/* An app that is broken or unfinished goes first, ahead of the greeting:
          nobody scrolls a dashboard to find out their bot has not been posting
          for a week. It only appears while something is genuinely blocking —
          a missing required step or a broken channel — and removes itself the
          moment that is resolved. */}
      <AppWidgets storeId={storeId || ''} slot="top" />

      <GreetingBar
        storeName={currentStore?.name ?? ''}
        testing={!!currentStore?.testing}
        onVerify={() => go('/verification')}
      />

      <PeriodSelector
        value={period}
        onChange={setPeriod}
        onPrefetch={prefetchPeriod}
        isRefreshing={isRefreshing}
        createdAt={currentStore?.createdAt}
        lang={i18n.language}
        compareEnabled={compareEnabled}
        onToggleCompare={() => setCompareEnabled(c => !c)}
      />

      {/* ── Hero row: revenue hero + monthly goal ring ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        <HeroRevenue
          className="lg:col-span-2"
          revenue={revenue}
          deltaPct={revenueDelta}
          period={periodLabel}
          elapsed={elapsedLabel}
          series={revenueSeries}
          currency={currency}
          compareEnabled={compareEnabled}
        />
        <MonthlyGoalRing
          pct={goalPct}
          revenue={revenue}
          remaining={goalRemaining}
          goal={monthlyGoal}
          onEdit={() => {
            const v = prompt(t('milestones.setGoal', 'حدد هدفك الشهري (بالريال):'), String(monthlyGoal || ''));
            if (v !== null && !Number.isNaN(Number(v)) && Number(v) >= 0) {
              localStorage.setItem(goalKey, v);
              window.location.reload();
            }
          }}
        />
      </div>

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
        <KpiCard
          Icon={DollarSign}
          label={t('dashboard.revenue', 'الإيرادات')}
          value={<FmtSar amount={revenue} />}
          delta={revenueDelta}
          series={revenueSeries}
          onClick={() => go('/analytics')}
        />
        <KpiCard
          Icon={ShoppingCart}
          label={t('dashboard.orders', 'الطلبات')}
          value={<FmtNum amount={orders} />}
          delta={ordersDelta}
          series={ordersSeries}
          onClick={() => go('/orders')}
        />
        <KpiCard
          Icon={Users}
          label={t('dashboard.customers', 'العملاء')}
          value={<FmtNum amount={customers} />}
          delta={customersDelta}
          series={ordersSeries}
          onClick={() => go('/customers')}
        />
        <KpiCard
          Icon={TrendingUp}
          label={t('dashboard.avgOrder', 'متوسط الطلب')}
          value={<FmtSar amount={avgOrder} />}
          delta={avgDelta}
          series={revenueSeries}
        />
      </div>

      {/* ── Sales chart + Quick actions + Traffic donut ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        <SalesChartCard
          className="lg:col-span-2"
          chartData={chartData}
          currency={currency}
          onNav={() => go('/analytics')}
        />
        <div className="flex flex-col gap-6">
          <QuickActionsCard onGo={go} />
          <TrafficDonutCard sources={trafficSources} total={trafficData?.uniqueVisitors ?? 0} />
        </div>
      </div>

      {/* ── Top Products + Setup Checklist ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <TopProductsCard
          items={topProducts}
          onGo={go}
        />
        <SetupChecklistCard
          steps={setupSteps}
          pct={setupPct}
          done={setupDone}
          total={setupSteps.length}
          onGo={go}
          insightsProps={{
            chartData,
            newCustomers: analyticsData?.customers?.new ?? 0,
            returningCustomers: analyticsData?.customers?.returning ?? 0,
            topProduct: topProducts[0],
            lang: i18n.language,
            periodLabel,
          }}
        />
      </div>

      {/* ── Cards contributed by installed apps ── */}
      <AppWidgets storeId={storeId || ''} />

      {/* ── Recent Orders table ── */}
      <RecentOrdersTable
        orders={filteredOrders}
        filter={orderFilter}
        setFilter={setOrderFilter}
        onGo={go}
        lang={i18n.language}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Greeting
   ════════════════════════════════════════════════════════════ */

function GreetingBar({ storeName, testing, onVerify }: { storeName: string; testing: boolean; onVerify: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-[22px] 2xl:text-[24px] font-bold leading-tight">
          {t('dashboard.greetingInStore', 'مرحباً بك في')} <span style={{ color: 'var(--color-pop-fg)' }}>{storeName || '-'}</span> 👋
        </h1>
        <p className="mt-1 text-[13px] text-text-muted">
          {t('dashboard.greetingSub', 'عساك طيب، حنا وياك. نبدأ على بركة الله.')}
        </p>
      </div>
      {testing ? (
        <button
          type="button"
          onClick={onVerify}
          className="pill cursor-pointer transition-opacity hover:opacity-80"
          style={{
            background: 'rgba(251,191,36,0.18)',
            color: '#fbbf24',
            border: '1px solid rgba(251,191,36,0.35)',
          }}
        >
          {t('dashboard.testingStore', 'وضع التجربة')}
        </button>
      ) : (
        <span className="pill pill-success">
          <span className="live-dot" style={{ marginInlineEnd: 4 }} />
          {t('dashboard.liveStore', 'المتجر مفعّل')}
        </span>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Hero: revenue + area sparkline
   ════════════════════════════════════════════════════════════ */

function HeroRevenue({ className, revenue, deltaPct, period, elapsed, series, currency, compareEnabled }:
{ className?: string; revenue: number; deltaPct: number | null; period: string; elapsed?: string; series: number[]; currency: string; compareEnabled: boolean }) {
  const { t } = useTranslation();
  const _ = currency; // kept for future; SAR is the only unit rendered today
  void _;

  // When there's revenue we replace the static marketing nudge with a
  // delta-driven sentence; the nudge only helps on truly empty stores.
  // When the user has switched off Compare we skip the "unavailable" line —
  // we don't want to nag them about something they chose to hide.
  let hint = '';
  if (revenue > 0 && deltaPct != null && Number.isFinite(deltaPct)) {
    const abs = Math.abs(deltaPct).toFixed(1);
    if (deltaPct > 0) hint = t('dashboard.deltaUp', 'زادت إيراداتك {{pct}}% عن الفترة الماضية', { pct: abs });
    else if (deltaPct < 0) hint = t('dashboard.deltaDown', 'انخفضت إيراداتك {{pct}}% عن الفترة الماضية', { pct: abs });
    else hint = t('dashboard.deltaFlat', 'إيراداتك ثابتة مقارنة بالفترة الماضية');
  } else if (revenue > 0 && compareEnabled) {
    hint = t('dashboard.deltaUnavailable', 'لا تتوفر مقارنة بالفترة الماضية بعد.');
  } else if (revenue === 0) {
    hint = t('dashboard.heroHint', 'ما ينقصك إلا التسويق. نبض المتجر يبدأ هنا.');
  }

  return (
    <div className={clsx('ui-hero p-6 2xl:p-8 relative flex flex-col justify-between min-h-[220px]', className)}>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="ui-hero-label text-[12px] font-semibold" style={{ letterSpacing: '0.08em' }}>
            {t('dashboard.totalRevenue', 'إجمالي الإيرادات')}
          </span>
          <span className="pill pill-info" style={{ padding: '3px 8px', fontSize: 10.5 }}>
            {period}
          </span>
          {elapsed && (
            <span className="text-[11px] text-text-muted">· {elapsed}</span>
          )}
        </div>
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <span className="ui-hero-value ui-num-xl">
            <FmtSar amount={revenue} />
          </span>
          <DeltaPill pct={deltaPct} />
        </div>
        {hint && (
          <p className="ui-hero-hint mt-2 text-[12.5px]">
            {hint}
          </p>
        )}
      </div>

      {/* Inline area sparkline, full width */}
      <div className="mt-5 -mb-1 relative h-[60px]">
        <div className="absolute inset-0">
          <Sparkline points={series.length ? series : [0,0,0,0,0,0,0,0,0,0,0,0,0,0]} color="var(--color-accent-pop)" width={720} height={60} />
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Monthly goal - conic ring
   ════════════════════════════════════════════════════════════ */

function MonthlyGoalRing({ pct, revenue, remaining, goal, onEdit }:
{ pct: number; revenue: number; remaining: number; goal: number; onEdit: () => void }) {
  const { t } = useTranslation();
  const hasProgress = revenue > 0 || goal > 0;

  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const daysLeft = Math.max(0, daysInMonth - new Date().getDate());

  return (
    <div className="ui-card p-6 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[14px] font-bold">{t('milestones.monthlyGoal', 'الهدف الشهري')}</h2>
        <button onClick={onEdit} className="text-[11px] text-text-muted hover:text-accent-pop transition-colors">
          {t('common.edit', 'تعديل')}
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-4 py-2">
        {/* Conic ring */}
        <div className="relative w-[156px] h-[156px]">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(#D2E9FE ${pct * 3.6}deg, var(--color-bg-subtle) ${pct * 3.6}deg)`,
              transition: 'background 0.6s ease',
            }}
          />
          <div
            className="absolute rounded-full flex flex-col items-center justify-center"
            style={{ inset: 10, background: 'var(--color-bg-secondary)' }}
          >
            <span className="ui-num-lg text-text-primary">{pct}%</span>
            <span className="text-[10.5px] text-text-muted mt-0.5">
              {daysLeft} {t('milestones.daysLeft', 'يوم متبقي')}
            </span>
          </div>
        </div>

        <div className="text-center">
          <div className="text-[12px] text-text-muted">
            {hasProgress ? (
              <>
                {t('milestones.remaining', 'المتبقي')}:{' '}
                <span className="num font-semibold text-text-secondary"><FmtCompact amount={remaining} /> <SarIcon /></span>
              </>
            ) : (
              t('milestones.setGoalHint', 'ابدأ برفع أول منتج لتفعيل الهدف.')
            )}
          </div>

          {/* Khaleeji nudge pill - hide when zero */}
          {revenue > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5"
                 style={{ background: 'var(--color-accent-light)', color: 'var(--color-pop-fg)' }}>
              <span className="text-[11px] font-semibold">
                {pct < 50
                  ? t('milestones.nudgeStart', 'شدّ حيلك، البركة بالبداية 💪')
                  : pct < 100
                    ? t('milestones.nudgeMid', 'ماشي على الطريق، يا هلا 🚀')
                    : t('milestones.nudgeDone', 'ما شاء الله تبارك الله 🎉')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   KPI card - icon chip + label + big value + delta + sparkline
   ════════════════════════════════════════════════════════════ */

function KpiCard({ Icon, label, value, delta, series, onClick }:
{ Icon: LucideIcon; label: string; value: React.ReactNode; delta: number | null; series: number[]; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={clsx(
        'ui-card p-5 text-start flex flex-col gap-3 transition-transform',
        onClick && 'cursor-pointer hover:-translate-y-0.5',
        !onClick && 'cursor-default',
      )}
    >
      <div className="flex items-center justify-between">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{ background: 'var(--color-accent-light)', color: 'var(--color-accent-pop)' }}
        >
          <Icon className="h-[17px] w-[17px]" />
        </div>
        <DeltaPill pct={delta} />
      </div>
      <div>
        <div className="text-[12px] font-semibold text-text-muted">{label}</div>
        <div className="mt-1 ui-num-lg text-text-primary">{value}</div>
      </div>
      <div className="mt-auto">
        <Sparkline points={series.length ? series : [0,0,0,0,0,0,0,0,0,0,0,0,0,0]} color="var(--color-accent-pop)" width={200} height={32} />
      </div>
    </button>
  );
}

/* ════════════════════════════════════════════════════════════
   Sales chart - area line, gradient fill, dashed grid, y-axis
   ════════════════════════════════════════════════════════════ */

function SalesChartCard({ className, chartData, currency, onNav }:
{ className?: string; chartData: { date: string; revenue: number; orders?: number; visits?: number }[]; currency: string; onNav: () => void }) {
  const { t } = useTranslation();
  const data = chartData.length ? chartData : Array.from({ length: 14 }, (_, i) => ({ date: `D${i + 1}`, revenue: 0 }));

  return (
    <div className={clsx('ui-card p-6 flex flex-col', className)}>
      <CardHead
        title={t('widgets.salesChart', 'مبيعات آخر فترة')}
        action={
          <div className="flex items-center gap-3">
            <LegendDot label={t('dashboard.revenue', 'الإيرادات')} />
            <button onClick={onNav} className="text-[11.5px] text-text-muted hover:text-accent-pop transition-colors">
              {t('common.viewAll', 'عرض الكل')}
            </button>
          </div>
        }
      />
      <div className="flex-1 min-h-[260px]">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="dashHeroFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="var(--color-pop-fg)" stopOpacity="0.22" />
                <stop offset="100%" stopColor="var(--color-pop-fg)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--color-bg-subtle)" strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(v: string) => typeof v === 'string' ? v.slice(5) : String(v)}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              dy={6}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v)}
              width={44}
            />
            <Tooltip
              cursor={{ stroke: 'var(--color-pop-fg)', strokeOpacity: 0.35, strokeDasharray: '3 3' }}
              contentStyle={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-strong)',
                borderRadius: 12,
                fontSize: 12,
                color: 'var(--color-text-primary)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}
              labelStyle={{ color: 'var(--color-text-muted)', marginBottom: 4 }}
              formatter={(val: any) => [`${intFmt.format(Number(val) || 0)} ${currency}`, t('dashboard.revenue', 'الإيرادات')]}
            />
            <Area type="monotone" dataKey="revenue" stroke="var(--color-pop-fg)" strokeWidth={2} fill="url(#dashHeroFill)" dot={false} activeDot={{ r: 4, fill: 'var(--color-pop-fg)', strokeWidth: 0 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function LegendDot({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-text-muted">
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: 'var(--color-pop-fg)' }} />
      {label}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════
   Quick actions + Traffic donut
   ════════════════════════════════════════════════════════════ */

interface QuickAction { labelKey: string; fallback: string; path: string; Icon: LucideIcon; }
const QUICK_ACTIONS: QuickAction[] = [
  { labelKey: 'sidebar.products',   fallback: 'المنتجات',      path: '/products/create',  Icon: Package },
  { labelKey: 'sidebar.coupons',    fallback: 'القسائم',       path: '/coupons',          Icon: Ticket },
  { labelKey: 'sidebar.marketing',  fallback: 'التسويق',       path: '/marketing',        Icon: Megaphone },
  { labelKey: 'sidebar.analytics',  fallback: 'التحليلات',     path: '/analytics',        Icon: Globe },
];

function QuickActionsCard({ onGo }: { onGo: (path: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="ui-card p-5">
      <CardHead title={t('widgets.quickNav', 'إجراءات سريعة')} />
      <div className="grid grid-cols-2 gap-2">
        {QUICK_ACTIONS.map(a => (
          <button
            key={a.path}
            onClick={() => onGo(a.path)}
            className="group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[12.5px] font-semibold text-text-secondary hover:text-text-primary transition-colors"
            style={{ background: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-default)' }}
          >
            <span
              className="flex h-7 w-7 items-center justify-center rounded-lg shrink-0"
              style={{ background: 'var(--color-accent-light)', color: 'var(--color-pop-fg)' }}
            >
              <a.Icon className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 text-start truncate">{t(a.labelKey, a.fallback)}</span>
            <Plus className="h-3.5 w-3.5 text-text-muted group-hover:text-accent-pop" />
          </button>
        ))}
      </div>
    </div>
  );
}

function TrafficDonutCard({ sources, total }: { sources?: { source: string; visits: number; percentage: number }[]; total: number }) {
  const { t } = useTranslation();
  const rows = (sources ?? []).slice(0, 4);
  const hasData = rows.length > 0 && rows.some(r => r.visits > 0);
  const pieData = hasData ? rows : [{ source: '-', visits: 1, percentage: 100 }];

  const palette = ['var(--color-pop-fg)', 'var(--color-teal)', 'var(--color-warm)', 'var(--color-steel)'];

  return (
    <div className="ui-card p-5">
      <CardHead title={t('widgets.traffic', 'حركة المرور')} />
      <div className="flex items-center gap-5">
        <div className="relative w-[108px] h-[108px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="visits"
                innerRadius={38}
                outerRadius={52}
                paddingAngle={hasData ? 3 : 0}
                stroke="none"
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={hasData ? palette[i % palette.length] : 'var(--color-bg-subtle)'} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[16px] font-bold text-text-primary num">{intFmt.format(total)}</span>
            <span className="text-[9.5px] text-text-muted">{t('dashboard.visits', 'الزيارات')}</span>
          </div>
        </div>
        <div className="flex-1 space-y-1.5 min-w-0">
          {(hasData ? rows : []).map((r, i) => (
            <div key={r.source + i} className="flex items-center gap-2 text-[11.5px]">
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: palette[i % palette.length] }} />
              <span className="flex-1 truncate text-text-secondary">{r.source}</span>
              <span className="num text-text-muted">{(r.percentage ?? 0).toFixed(0)}%</span>
            </div>
          ))}
          {!hasData && (
            <p className="text-[11.5px] text-text-muted">{t('dashboard.trafficEmpty', 'لا توجد زيارات بعد، شارك رابط متجرك.')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Top products + Setup checklist
   ════════════════════════════════════════════════════════════ */

function TopProductsCard({ items, onGo }: { items: { productId: string; title: string; revenue: number; sales: number }[]; onGo: (p: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="ui-card p-5 flex flex-col">
      <CardHead
        title={t('widgets.topProducts', 'المنتجات الأكثر مبيعاً')}
        action={<button onClick={() => onGo('/analytics')} className="text-[11.5px] text-text-muted hover:text-accent-pop transition-colors">{t('common.viewAll', 'عرض الكل')}</button>}
      />
      {items.length === 0 ? (
        <EmptyBlock text={t('dashboard.topProductsEmpty', 'ستظهر منتجاتك الأكثر مبيعاً هنا بعد أول طلب.')} />
      ) : (
        <div className="flex-1 flex flex-col gap-1.5">
          {items.slice(0, 5).map((p, i) => (
            <button
              key={p.productId}
              onClick={() => onGo(`/products/${p.productId}`)}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-bg-hover transition-colors text-start"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg num text-[11.5px] font-bold"
                style={{
                  background: i === 0 ? 'var(--color-pop-fg)' : 'var(--color-accent-light)',
                  color: i === 0 ? 'var(--color-accent-ink)' : 'var(--color-pop-fg)',
                }}
              >
                {i + 1}
              </span>
              <span className="flex-1 text-[13px] font-medium text-text-primary truncate">{p.title}</span>
              <div className="text-end shrink-0">
                <div className="text-[13px] font-semibold text-text-primary"><FmtSar amount={p.revenue} /></div>
                <div className="text-[10.5px] text-text-muted">{t('dashboard.soldCount', '{{count}} مباع', { count: p.sales })}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightsCard({ chartData, newCustomers, returningCustomers, topProduct, lang, periodLabel }:
{ chartData: { date: string; revenue: number; orders?: number }[]; newCustomers: number; returningCustomers: number; topProduct?: { title: string; revenue: number }; lang: string; periodLabel: string }) {
  const { t } = useTranslation();

  // Best day in the visible chart window.
  const best = chartData.reduce<{ date: string; revenue: number } | null>((acc, p) => {
    if (!p || !p.revenue) return acc;
    if (!acc || p.revenue > acc.revenue) return { date: p.date, revenue: p.revenue };
    return acc;
  }, null);

  const formatBestDate = (iso: string): string => {
    // 'YYYY-MM' (year/month buckets) renders as month name; 'YYYY-MM-DD' as
    // a localized short date. Falls back to the raw string on parse error.
    const tag = lang === 'ar' ? 'ar' : 'en';
    try {
      if (/^\d{4}-\d{2}$/.test(iso)) {
        const [y, m] = iso.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString(tag, { year: 'numeric', month: 'long' });
      }
      return new Date(iso).toLocaleDateString(tag, { day: 'numeric', month: 'short' });
    } catch { return iso; }
  };

  const totalCustomers = newCustomers + returningCustomers;
  const returnRate = totalCustomers > 0 ? Math.round((returningCustomers / totalCustomers) * 100) : null;

  return (
    <div className="ui-card p-5 flex flex-col">
      <CardHead title={t('insights.title', 'نظرة سريعة')} emoji="✨" />
      <p className="-mt-3 mb-4 text-[11px] text-text-muted">
        {t('insights.scope', 'خلال {{period}}', { period: periodLabel })}
      </p>
      <div className="flex-1 flex flex-col gap-3">
        <InsightRow
          label={t('insights.bestDay', 'أفضل يوم')}
          value={best ? <FmtSar amount={best.revenue} /> : '—'}
          hint={best ? formatBestDate(best.date) : t('insights.noBestDay', 'لا توجد مبيعات في هذه الفترة بعد')}
        />
        <InsightRow
          label={t('insights.returnRate', 'نسبة العملاء العائدين')}
          value={returnRate != null ? `${returnRate}%` : '—'}
          hint={totalCustomers > 0
            ? t('insights.returnHint', '{{returning}} عائد من أصل {{total}}', { returning: returningCustomers, total: totalCustomers })
            : t('insights.noCustomers', 'لا يوجد عملاء بعد')}
        />
        <InsightRow
          label={t('insights.topProduct', 'الأكثر مبيعاً')}
          value={topProduct ? <FmtSar amount={topProduct.revenue} /> : '—'}
          hint={topProduct?.title || t('insights.noTopProduct', 'لا يوجد منتج بعد')}
        />
      </div>
    </div>
  );
}

function InsightRow({ label, value, hint }: { label: string; value: React.ReactNode; hint: string }) {
  return (
    <div className="rounded-xl p-3" style={{ border: '1px solid var(--color-border-default)' }}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11.5px] font-semibold text-text-muted">{label}</span>
        <span className="text-[14px] font-bold text-text-primary num">{value}</span>
      </div>
      <p className="mt-1 text-[11px] text-text-muted truncate">{hint}</p>
    </div>
  );
}

function SetupChecklistCard({ steps, pct, done, total, onGo, insightsProps }:
{ steps: { id: string; labelKey: string; descKey: string; action: string; done: boolean; icon: LucideIcon }[]; pct: number; done: number; total: number; onGo: (p: string) => void; insightsProps: React.ComponentProps<typeof InsightsCard> }) {
  const { t } = useTranslation();

  if (done === total) {
    return <InsightsCard {...insightsProps} />;
  }

  return (
    <div className="ui-card p-5 flex flex-col">
      <CardHead
        title={t('setup.title', 'إعداد المتجر')}
        action={<span className="text-[11.5px] text-text-muted num">{done}/{total}</span>}
      />
      <div className="h-1.5 rounded-full overflow-hidden mb-4" style={{ background: 'var(--color-bg-subtle)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: 'var(--color-pop-fg)' }} />
      </div>
      <div className="flex-1 flex flex-col gap-2">
        {steps.map(step => (
          <button
            key={step.id}
            onClick={() => !step.done && onGo(step.action)}
            disabled={step.done}
            className={clsx(
              'flex items-center gap-3 rounded-xl p-3 text-start transition-colors',
              step.done ? 'opacity-60' : 'hover:bg-bg-hover cursor-pointer',
            )}
            style={{ border: '1px solid var(--color-border-default)' }}
          >
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={step.done
                ? { background: 'rgba(30,143,94,0.18)', color: '#6be0a3' }
                : { background: 'var(--color-accent-light)', color: 'var(--color-pop-fg)' }
              }
            >
              {step.done ? <Check className="h-4 w-4" /> : <step.icon className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={clsx('text-[13px] font-semibold text-text-primary', step.done && 'line-through')}>{t(step.labelKey)}</p>
              <p className="text-[11.5px] text-text-muted">{t(step.descKey)}</p>
            </div>
            {!step.done && <ArrowUpRight className="h-3.5 w-3.5 text-text-muted shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Recent orders table
   ════════════════════════════════════════════════════════════ */

function RecentOrdersTable({ orders, filter, setFilter, onGo, lang }:
{ orders: any[]; filter: string; setFilter: (f: any) => void; onGo: (p: string) => void; lang: string }) {
  const { t } = useTranslation();
  // Same axis and the same labels as the orders page tabs. The old chips were
  // payment statuses, which this widget can never show: it only ever receives
  // paid orders, so 'pending'/'processing' always emptied the table.
  const filters: { id: any; label: string }[] = [
    { id: 'all', label: t('orders.filterAll', 'الكل') },
    ...DELIVERY_STATUSES.map((s) => ({ id: s, label: t(`orders.${s}`, s) })),
  ];

  return (
    <div className="ui-card p-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-[14px] font-bold">{t('widgets.recentOrders', 'الطلبات الأخيرة')}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="ui-rail flex flex-wrap gap-1">
            {filters.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={clsx(
                  'rounded-lg px-3 py-1.5 text-[11.5px] font-semibold transition-colors',
                  filter === f.id ? 'text-accent-ink' : 'text-text-muted hover:text-text-primary',
                )}
                style={filter === f.id ? { background: 'var(--color-pop-fg)' } : undefined}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={() => onGo('/orders')} className="text-[11.5px] text-text-muted hover:text-accent-pop transition-colors whitespace-nowrap">
            {t('common.viewAll', 'عرض الكل')}
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <EmptyBlock text={t('dashboard.ordersEmpty', 'لا توجد طلبات بعد، شارك رابط متجرك في قنوات التواصل.')} />
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-start">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-text-muted">
                <th className="px-2 py-2 text-start font-semibold">{t('orders.number', 'رقم الطلب')}</th>
                <th className="px-2 py-2 text-start font-semibold">{t('orders.customer', 'العميل')}</th>
                <th className="px-2 py-2 text-start font-semibold">{t('orders.city', 'المدينة')}</th>
                <th className="px-2 py-2 text-start font-semibold">{t('orders.time', 'منذ')}</th>
                <th className="px-2 py-2 text-start font-semibold">{t('orders.status', 'الحالة')}</th>
                <th className="px-2 py-2 text-end font-semibold">{t('orders.total', 'الإجمالي')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 8).map((o: any, idx: number) => {
                const city = o?.shippingAddress?.city ?? o?.customer?.city ?? '-';
                const time = o?.createdAt ? relativeTime(o.createdAt, lang) : '-';
                const statusValue = o.delivery || o.status;
                const statusClass = DELIVERY_STATUS_CLASSES[statusValue] ?? 'bg-bg-tertiary text-text-primary border-border-default';
                const orderId = o._id ?? o.id;
                const orderNumber = o.orderNumber ?? (orderId ? String(orderId).slice(-6) : '-');
                return (
                  <tr
                    key={orderId ?? idx}
                    onClick={() => orderId && onGo(`/orders/${orderId}`)}
                    className="cursor-pointer group"
                    style={{ transition: 'background 0.15s ease' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td className="px-2 py-3 border-t border-border-default">
                      <span className="mono text-[12.5px] font-semibold" style={{ color: 'var(--color-pop-fg)' }}>
                        #{orderNumber}
                      </span>
                    </td>
                    <td className="px-2 py-3 border-t border-border-default">
                      <span className="text-[12.5px] text-text-primary">{o?.customer?.name ?? '-'}</span>
                    </td>
                    <td className="px-2 py-3 border-t border-border-default">
                      <span className="text-[12.5px] text-text-secondary">{city}</span>
                    </td>
                    <td className="px-2 py-3 border-t border-border-default">
                      <span className="text-[11.5px] text-text-muted num">{time}</span>
                    </td>
                    <td className="px-2 py-3 border-t border-border-default">
                      <span className={clsx('inline-block rounded-md border px-2 py-1 text-[11.5px] font-medium', statusClass)}>
                        {t(`orders.${statusValue}`, { defaultValue: statusValue ?? '-' }) as string}
                      </span>
                    </td>
                    <td className="px-2 py-3 border-t border-border-default text-end">
                      <span className="text-[13px] font-semibold text-text-primary">
                        <FmtSar amount={o.total ?? 0} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Small shared pieces
   ════════════════════════════════════════════════════════════ */

function EmptyBlock({ text }: { text: string }) {
  return (
    <div className="flex-1 flex items-center justify-center py-10 text-center">
      <p className="text-[12.5px] text-text-muted max-w-xs">{text}</p>
    </div>
  );
}
